/**
 * Purchases: every shroom purchase, with a per-row invoice PDF download. The PDF
 * endpoint is authenticated, so it is fetched as a blob (same pattern as
 * accountApi.invoicePdf) and handed to the browser via an object URL.
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminPurchasePage } from '../../game-api';
import { fmtDateTime, fmtMoney, fmtNum, invoiceFilename, pageInfo, pageLabel } from './format';
import { apiMessage, btn, emptyBox, errorBox, input, panel, pill, saveBlob, spinner, table, toastError,
} from './ui';

const PAGE_SIZE = 20;

export function renderPurchases(host: HTMLElement): () => void {
  let cancelled = false;
  const state = { query: '', limit: PAGE_SIZE, offset: 0 };

  const search = input('Search by player or invoice…');
  const listHost = el('div', 'min-h-[80px]');
  const pager = el('div', 'flex items-center justify-between gap-2 mt-3 flex-wrap');

  let timer: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => { state.query = search.value.trim(); state.offset = 0; void load(); }, 300);
  });

  async function load() {
    clear(listHost); clear(pager);
    listHost.appendChild(spinner());
    try {
      const page = await adminApi.purchases({ ...state });
      if (cancelled) return;
      renderPage(page);
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load purchases."), () => void load()));
    }
  }

  async function download(id: string, invoiceNumber: string | null, button: HTMLButtonElement) {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = '…';
    try {
      const blob = await adminApi.purchaseInvoicePdf(id);
      saveBlob(blob, invoiceFilename(id, invoiceNumber));
    } catch (e) {
      toastError(e, 'Could not download the invoice');
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  /**
   * Issue a NUMBERED invoice for a purchase.
   *
   * The PDF endpoint always renders something, so "generate" here does not mean
   * "make a PDF" — it means give the purchase a durable, sequential Invoice row
   * instead of the derived TB-xxxx reference. Real-money purchases only: the
   * server refuses anything without a completed Payment behind it, and its
   * message is surfaced verbatim rather than swallowed. Re-issuing is safe — the
   * endpoint is idempotent and hands back the existing number.
   */
  async function generate(id: string, button: HTMLButtonElement) {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = '…';
    try {
      const row = await adminApi.generatePurchaseInvoice(id);
      toast(`Invoice ${row.invoiceNumber ?? ''} issued`.trim());
      await load();                       // repaint: the row now shows the real number
    } catch (e) {
      toastError(e, 'Could not issue an invoice for this purchase');
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  /** Re-run the credit for a payment whose Shrooms did not arrive. */
  async function retry(id: string, button: HTMLButtonElement) {
    button.disabled = true;
    const original = button.textContent;
    button.textContent = '…';
    try {
      const r = await adminApi.retryPurchaseCredit(id);
      if (r.status === 'CREDITED') toast(r.outcome === 'already' ? 'Already credited' : 'Shrooms credited');
      else toast(`Not credited: ${r.reason ?? r.lastError ?? r.status ?? 'unknown'}`, 'error');
      await load();
    } catch (e) {
      toastError(e, 'Could not retry the credit');
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  function renderPage(page: AdminPurchasePage) {
    const items = page.items ?? [];
    clear(listHost); clear(pager);
    if (!items.length) { listHost.appendChild(emptyBox('No purchases recorded yet.')); return; }

    listHost.appendChild(table(
      ['Date', 'Player', 'Shrooms', 'Amount', 'Status', 'Invoice', ''],
      items.map((p) => {
        const dl = btn('Download PDF', () => void download(p.id, p.invoiceNumber, dl));
        const status = (p.status ?? '').toUpperCase();
        const payable = status === 'COMPLETED' || status === 'PAID';
        // Three states per row: already numbered (download only), invoiceable
        // (offer to issue), or nothing behind it (say so instead of a button
        // that can only fail).
        const gen = p.invoiceIssued
          ? null
          : payable
            ? btn('Generate invoice', () => void generate(p.id, gen as HTMLButtonElement), 'primary')
            : null;
        const note = !p.invoiceIssued && !payable
          ? el('span', 'text-[11px] text-text-dim', {}, 'Not invoiceable')
          : null;
        return [
          fmtDateTime(p.createdAt),
          el('div', 'min-w-0', {},
            // The API nests the buyer under `user`; older shapes sent it flat.
            el('div', '', {}, p.user?.username ?? p.nickname ?? '—'),
            el('div', 'text-[11px] text-text-dim', {}, p.user?.email ?? p.email ?? '')),
          fmtNum(p.shrooms),
          fmtMoney(p.amountEur),
          el('div', 'flex flex-col gap-1 items-start min-w-0', {},
            pill(status || 'unknown', status === 'COMPLETED' || status === 'PAID' ? 'ok' : status === 'FAILED' ? 'bad' : 'warn'),
            // Paid is not the same as credited: a gateway payment can settle and
            // still need a human (mismatch vs the quote, a failed credit).
            p.fulfilmentStatus && p.fulfilmentStatus !== 'CREDITED'
              ? pill(p.fulfilmentStatus === 'REVIEW' ? 'NEEDS REVIEW' : p.fulfilmentStatus === 'FAILED' ? 'CREDIT FAILED' : 'CREDIT PENDING',
                p.needsReview ? 'bad' : 'warn')
              : null,
            p.fulfilmentNote && p.fulfilmentStatus !== 'CREDITED'
              ? el('span', 'text-[10px] text-text-dim break-words max-w-[220px]', {}, p.fulfilmentNote)
              : null),
          el('div', 'flex flex-col gap-0.5 min-w-0', {},
            el('span', `font-mono text-xs ${p.invoiceIssued ? 'text-[#ffd97a]' : 'text-text-dim'}`, {}, p.invoiceNumber ?? '—'),
            p.invoiceIssued ? null : el('span', 'text-[10px] text-text-dim', {}, 'not issued')),
          el('div', 'flex items-center gap-2', {}, gen, note,
            p.needsReview ? (() => { const r = btn('Retry credit', () => void retry(p.id, r)); return r; })() : null,
            dl),
        ];
      })));

    const info = pageInfo(page.total ?? items.length, page.limit || PAGE_SIZE, page.offset ?? state.offset);
    pager.append(
      el('div', 'text-xs text-text-dim font-mono', {}, pageLabel(page.total ?? items.length, page.limit || PAGE_SIZE, page.offset ?? state.offset)),
      el('div', 'flex gap-2 items-center', {},
        pageBtn('Prev', info.hasPrev, () => { state.offset = Math.max(0, state.offset - state.limit); void load(); }),
        el('span', 'text-xs text-text-dim font-mono', {}, `Page ${info.page} / ${info.pageCount}`),
        pageBtn('Next', info.hasNext, () => { state.offset += state.limit; void load(); })));
  }

  host.appendChild(panel('Shroom purchases',
    el('div', 'flex flex-wrap items-center gap-2 mb-3', {}, el('div', 'flex-1 min-w-[180px]', {}, search)),
    listHost, pager));
  void load();
  return () => { cancelled = true; clearTimeout(timer); };
}

function pageBtn(label: string, enabled: boolean, onClick: () => void): HTMLButtonElement {
  const b = btn(label, () => { if (enabled) onClick(); });
  if (!enabled) { b.disabled = true; b.className += ' opacity-40 cursor-not-allowed'; }
  return b;
}
